import { useState, useMemo } from "react"
import { router } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { FolderOpen, Link, Unlink, Info, Plus, Pencil, Trash2 } from "lucide-react"
import { FolderPicker } from "@/components/capture/FolderPicker"
import { FieldModal } from "./FieldModal"
import { RuleModal } from "./RuleModal"
import type { UserSettings, FrontMatterField, RoutingRule } from "./SettingsPage"

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

const TEMPLATE_TAGS = [
  { tag: "{YYYY}", description: "Year" },
  { tag: "{MM}", description: "Month" },
  { tag: "{DD}", description: "Day" },
  { tag: "{HH}", description: "Hour" },
  { tag: "{mm}", description: "Minute" },
  { tag: "{input}", description: "User input" },
]

const RETENTION_OPTIONS = [
  { value: "7_days", label: "7 days" },
  { value: "30_days", label: "30 days" },
  { value: "90_days", label: "90 days" },
  { value: "forever", label: "Keep forever" },
]

type FolderPickerTarget =
  | "root_folder"
  | "text_notes_destination"
  | "voice_notes_destination"
  | "uploads_destination"
  | "images_folder"
  | null

interface FilesTabProps {
  settings: UserSettings
  dropboxConnected: boolean
  fields: FrontMatterField[]
  routingRules: RoutingRule[]
}

export function FilesTab({ settings, dropboxConnected, fields: initialFields, routingRules: initialRules }: FilesTabProps) {
  const [localSettings, setLocalSettings] = useState(settings)
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [activePicker, setActivePicker] = useState<FolderPickerTarget>(null)

  // Front matter fields state
  const [fields, setFields] = useState<FrontMatterField[]>(initialFields)
  const [rules, setRules] = useState<RoutingRule[]>(initialRules)
  const [fieldModalOpen, setFieldModalOpen] = useState(false)
  const [editingField, setEditingField] = useState<FrontMatterField | null>(null)
  const [ruleModalOpen, setRuleModalOpen] = useState(false)

  // File name template state
  const [template, setTemplate] = useState(settings.file_name_template)
  const [saveAudio, setSaveAudio] = useState(settings.save_audio_to_dropbox)
  const [retention, setRetention] = useState(settings.audio_retention_period)

  const preview = useMemo(() => {
    const now = new Date()
    return template
      .replace("{YYYY}", String(now.getFullYear()))
      .replace("{MM}", String(now.getMonth() + 1).padStart(2, "0"))
      .replace("{DD}", String(now.getDate()).padStart(2, "0"))
      .replace("{HH}", String(now.getHours()).padStart(2, "0"))
      .replace("{mm}", String(now.getMinutes()).padStart(2, "0"))
      .replace("{input}", "untitled")
  }, [template])

  // --- Settings update helpers ---

  async function updateSetting(key: string, value: string | boolean) {
    await fetch("/api/user_settings", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
      body: JSON.stringify({ [key]: value }),
    })
  }

  function openPicker(field: FolderPickerTarget) {
    setActivePicker(field)
    setFolderPickerOpen(true)
  }

  async function handleFolderSelect(path: string) {
    if (!activePicker) return
    setFolderPickerOpen(false)
    const updated = { ...localSettings, [activePicker]: path }
    setLocalSettings(updated)
    await updateSetting(activePicker, path)
  }

  // --- Dropbox ---

  function handleConnectDropbox() {
    window.location.href = "/auth/dropbox"
  }

  function handleDisconnectDropbox() {
    if (!confirm("Disconnect Dropbox? Your files will remain in Dropbox but the app won't be able to access them.")) return
    router.delete("/auth/dropbox")
  }

  // --- Front matter fields ---

  function handleAddField() {
    setEditingField(null)
    setFieldModalOpen(true)
  }

  function handleEditField(field: FrontMatterField) {
    setEditingField(field)
    setFieldModalOpen(true)
  }

  async function handleDeleteField(field: FrontMatterField) {
    if (!confirm(`Delete field "${field.name}" and all its options?`)) return
    await fetch(`/api/front_matter_fields/${field.id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": getCSRFToken() },
    })
    setFields(fields.filter((f) => f.id !== field.id))
    setRules(rules.filter((r) => r.front_matter_field_id !== field.id))
  }

  function handleFieldSaved(savedField: FrontMatterField) {
    if (editingField) {
      setFields(fields.map((f) => (f.id === savedField.id ? savedField : f)))
    } else {
      setFields([...fields, savedField])
    }
    setFieldModalOpen(false)
  }

  // --- Routing rules ---

  async function handleDeleteRule(rule: RoutingRule) {
    if (!confirm("Delete this routing rule?")) return
    await fetch(`/api/routing_rules/${rule.id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": getCSRFToken() },
    })
    setRules(rules.filter((r) => r.id !== rule.id))
  }

  function handleRuleSaved(savedRule: RoutingRule) {
    setRules([...rules, savedRule])
    setRuleModalOpen(false)
  }

  // --- File name template ---

  function handleTemplateBlur() {
    updateSetting("file_name_template", template)
  }

  function handleTagClick(tag: string) {
    const newTemplate = template + tag
    setTemplate(newTemplate)
    updateSetting("file_name_template", newTemplate)
  }

  // --- Audio ---

  function handleAudioToggle(checked: boolean) {
    setSaveAudio(checked)
    updateSetting("save_audio_to_dropbox", checked)
  }

  function handleRetentionChange(value: string) {
    setRetention(value)
    updateSetting("audio_retention_period", value)
  }

  // --- Folder picker helpers ---

  function currentPickerPath(): string {
    if (!activePicker) return ""
    return (localSettings as Record<string, string>)[activePicker] || ""
  }

  const folderFields: Array<{
    key: FolderPickerTarget
    label: string
    settingsKey: keyof UserSettings
    tooltip?: string
  }> = [
    { key: "root_folder", label: "Root Folder", settingsKey: "root_folder", tooltip: "All folder trees start here" },
    { key: "text_notes_destination", label: "Text notes default destination", settingsKey: "text_notes_destination" },
    { key: "voice_notes_destination", label: "Voice notes default destination", settingsKey: "voice_notes_destination" },
    { key: "uploads_destination", label: "File & image uploads destination", settingsKey: "uploads_destination" },
    { key: "images_folder", label: "Image assets folder", settingsKey: "images_folder", tooltip: "Where to store images you insert into markdown files" },
  ]

  return (
    <div className="space-y-6">
      {/* 1. Dropbox Connection */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold">Dropbox Connection</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect your Dropbox account to save and sync files.
        </p>
        <Separator className="my-4" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {dropboxConnected ? (
              <Badge variant="secondary" className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                Connected
              </Badge>
            ) : (
              <Badge variant="secondary">Not connected</Badge>
            )}
          </div>
          {dropboxConnected ? (
            <Button variant="outline" size="sm" onClick={handleDisconnectDropbox}>
              <Unlink className="mr-2 h-4 w-4" />
              Disconnect
            </Button>
          ) : (
            <Button size="sm" onClick={handleConnectDropbox}>
              <Link className="mr-2 h-4 w-4" />
              Connect Dropbox
            </Button>
          )}
        </div>
      </Card>

      {/* 2. Folder Destinations */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold">Folder Destinations</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Set the default folders for your files.
        </p>
        <Separator className="my-4" />
        <div className="space-y-4">
          {folderFields.map(({ key, label, settingsKey, tooltip }) => (
            <div key={key}>
              <div className="flex items-center gap-1.5">
                <Label>{label}</Label>
                {tooltip && (
                  <Info className="h-3.5 w-3.5 text-muted-foreground" title={tooltip} />
                )}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded bg-muted px-3 py-2 text-sm">
                  {dropboxConnected
                    ? (localSettings[settingsKey] as string) || "/"
                    : "—"}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openPicker(key)}
                  disabled={!dropboxConnected}
                >
                  <FolderOpen className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* 3. Front Matter Fields */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Front Matter Fields</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Define metadata fields that appear when saving files.
            </p>
          </div>
          <Button size="sm" onClick={handleAddField}>
            <Plus className="mr-2 h-4 w-4" />
            Add Field
          </Button>
        </div>
        <Separator className="my-4" />
        {fields.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No front matter fields configured yet.
          </p>
        ) : (
          <div className="space-y-3">
            {fields.map((field) => (
              <div key={field.id} className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="font-medium">{field.name}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {field.options.map((opt) => (
                      <span key={opt.id} className="inline-block rounded bg-muted px-2 py-0.5 text-xs">
                        {opt.label}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEditField(field)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteField(field)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 4. Routing Rules */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Routing Rules</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Automatically set the destination folder based on front matter selections.
            </p>
          </div>
          <Button size="sm" onClick={() => setRuleModalOpen(true)} disabled={fields.length === 0}>
            <Plus className="mr-2 h-4 w-4" />
            Add Rule
          </Button>
        </div>
        <Separator className="my-4" />
        {rules.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No routing rules configured yet.
          </p>
        ) : (
          <div className="space-y-3">
            {rules.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between rounded-lg border p-3">
                <div className="text-sm">
                  When <span className="font-medium">{rule.field_name}</span> is{" "}
                  <span className="font-medium">{rule.option_label}</span> → save to{" "}
                  <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{rule.destination_folder}</code>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteRule(rule)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 5. File Name Template */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold">File Name Template</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure how file names are generated when saving.
        </p>
        <Separator className="my-4" />
        <div className="space-y-4">
          <div>
            <Label>Template</Label>
            <Input
              className="mt-1 font-mono"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              onBlur={handleTemplateBlur}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Available tags</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {TEMPLATE_TAGS.map(({ tag, description }) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => handleTagClick(tag)}
                >
                  <code className="text-xs">{tag}</code>
                  <span className="ml-1 text-xs text-muted-foreground">{description}</span>
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Preview</Label>
            <div className="mt-1 rounded bg-muted px-3 py-2 font-mono text-sm">
              {preview}.md
            </div>
          </div>
        </div>
      </Card>

      {/* 6. Audio File Settings */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold">Audio File Settings</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure how audio recordings are handled.
        </p>
        <Separator className="my-4" />
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Save audio files to Dropbox</Label>
              <p className="text-sm text-muted-foreground">
                Keep original audio alongside transcription markdown.
              </p>
            </div>
            <Switch checked={saveAudio} onCheckedChange={handleAudioToggle} />
          </div>
          <div>
            <Label>Audio retention period</Label>
            <p className="mb-2 text-sm text-muted-foreground">
              How long to keep audio in cloud storage before auto-deletion.
            </p>
            <Select value={retention} onValueChange={handleRetentionChange}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RETENTION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Modals */}
      <FolderPicker
        open={folderPickerOpen}
        onClose={() => setFolderPickerOpen(false)}
        onSelect={handleFolderSelect}
        currentPath={currentPickerPath()}
        rootPath={activePicker === "root_folder" ? undefined : localSettings.root_folder}
      />

      <FieldModal
        open={fieldModalOpen}
        onClose={() => setFieldModalOpen(false)}
        onSave={handleFieldSaved}
        field={editingField}
      />

      <RuleModal
        open={ruleModalOpen}
        onClose={() => setRuleModalOpen(false)}
        onSave={handleRuleSaved}
        fields={fields}
        rootPath={localSettings.root_folder}
      />
    </div>
  )
}
